import React, {useMemo, useRef, useState} from "react";
import {Box} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TextFieldBox from "../../components/Common/TextFieldBox";
import {FieldLabel} from "../Onboarding/registrationShared";
import {
  getPasswordRuleChecks,
  validateConfirmPassword,
  validateRegistrationPassword,
} from "../../helper/validation";
import {errorDetail} from "../../helper/errorDetail";
import {
  BodyText,
  FormStack,
  Heading,
  IconTile,
  PrimaryButton,
  ValidationText,
} from "./styled";

export default function CreateVerificationPasswordForm({onSubmit}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const submissionStarted = useRef(false);
  const passwordRules = useMemo(() => getPasswordRuleChecks(password), [password]);
  const passwordError = validateRegistrationPassword(password);
  const passwordValid = passwordError === "";
  const passwordConstraintError = [
    "Password must be 100 characters or fewer",
    "Password cannot contain line breaks",
  ].includes(passwordError) ? passwordError : "";
  const passwordsMatch = validateConfirmPassword(password, confirmPassword) === "";
  const canSubmit = passwordValid && passwordsMatch && !submitting;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submissionStarted.current) return;

    submissionStarted.current = true;
    setSubmitting(true);
    setSubmissionError("");
    const response = await onSubmit(password);
    if (response?.handled) return;

    setSubmissionError(errorDetail(response?.data?.detail,
      "We could not create your password. Please try again."
    ));
    submissionStarted.current = false;
    setSubmitting(false);
  };

  return (
    <>
      <IconTile aria-hidden="true"><LockOutlinedIcon sx={{fontSize: 24}} /></IconTile>
      <Heading component="h1">Create your password</Heading>
      <BodyText>Choose a secure password to finish creating your account.</BodyText>
      <FormStack onSubmit={handleSubmit} noValidate>
        <Box>
          <FieldLabel htmlFor="verification-password">Password</FieldLabel>
          <TextFieldBox fullWidth size="small" type="password" name="password" value={password}
            placeholder="Create a password" handleChange={(event) => setPassword(event.target.value)}
            inputProps={{id: "verification-password", autoComplete: "new-password", "aria-describedby": "password-rules"}} />
        </Box>
        <Box
          component="ul"
          id="password-rules"
          data-testid="password-rules"
          aria-label="Password requirements"
          sx={{m: 0, p: 0, listStyle: "none", display: "grid", gap: 0.75}}
        >
          {passwordRules.map((rule) => (
            <Box component="li" key={rule.id} data-met={rule.met ? "true" : "false"}
              sx={{display: "flex", alignItems: "center", gap: 1, fontSize: 13, color: rule.met ? "#1B7F3B" : "#8692A6"}}>
              {rule.met
                ? <CheckCircleRoundedIcon aria-hidden="true" sx={{fontSize: 16}} />
                : <RadioButtonUncheckedRoundedIcon aria-hidden="true" sx={{fontSize: 16}} />}
              {rule.label}
            </Box>
          ))}
        </Box>
        <Box>
          <FieldLabel htmlFor="verification-confirm-password">Confirm password</FieldLabel>
          <TextFieldBox fullWidth size="small" type="password" name="confirm_password" value={confirmPassword}
            placeholder="Repeat your password" handleChange={(event) => setConfirmPassword(event.target.value)}
            inputProps={{id: "verification-confirm-password", autoComplete: "new-password"}} />
        </Box>
        {passwordConstraintError && (
          <ValidationText role="alert">{passwordConstraintError}</ValidationText>
        )}
        {submissionError && <ValidationText role="alert">{submissionError}</ValidationText>}
        <PrimaryButton type="submit" variant="contained" disabled={!canSubmit}>
          {submitting ? "Creating password…" : "Create password"}
        </PrimaryButton>
      </FormStack>
    </>
  );
}
